// Bus tipado cross-tab. Pequeno por design.
//
// Princípios:
//   - Escopo fixo: apenas auth/wallet/orders. Adicionar evento exige PR.
//   - Self-tab guard: cada aba ignora os próprios eventos (senderId).
//   - Debounce opcional para eventos de alta frequência.
//   - Fallback storage event quando BroadcastChannel não existir (Safari ITP velho, iframes sandbox).
//   - Cleanup explícito (close()).
//   - Nada de state. Esta camada só notifica — quem reage decide o que invalidar.

export type TabEvent =
  | { type: "auth:signout"; at: number }
  | { type: "auth:signin"; at: number; userId: string }
  | { type: "wallet:changed"; at: number; userId: string }
  | { type: "orders:changed"; at: number; userId: string };

export type TabEventType = TabEvent["type"];

const CHANNEL = "nickrobotic:tabsync:v1";
const STORAGE_KEY = "__nr_tabsync_v1";
// Identifica a aba atual para descartar eventos próprios. Recriado a cada load.
const SENDER_ID =
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2);

type Envelope = { sender: string; event: TabEvent };

type Listener = (event: TabEvent) => void;

class TabSync {
  private bc: BroadcastChannel | null = null;
  private listeners = new Set<Listener>();
  private boundStorage: ((e: StorageEvent) => void) | null = null;
  private lastDebounce = new Map<string, number>();

  start() {
    if (this.bc || this.boundStorage) return; // idempotente
    if (typeof window === "undefined") return;

    if (typeof BroadcastChannel !== "undefined") {
      try {
        this.bc = new BroadcastChannel(CHANNEL);
        this.bc.onmessage = (e) => this.handleEnvelope(e.data);
      } catch {
        this.bc = null;
      }
    }
    // storage event como fallback (e também como rede de segurança extra).
    this.boundStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY || !e.newValue) return;
      try {
        this.handleEnvelope(JSON.parse(e.newValue));
      } catch {
        /* ignore parse errors */
      }
    };
    window.addEventListener("storage", this.boundStorage);
  }

  stop() {
    this.bc?.close();
    this.bc = null;
    if (this.boundStorage) {
      window.removeEventListener("storage", this.boundStorage);
      this.boundStorage = null;
    }
    this.listeners.clear();
    this.lastDebounce.clear();
  }

  /** Publica um evento para outras abas. A aba origem NÃO recebe de volta. */
  publish(event: TabEvent, opts?: { debounceMs?: number }) {
    if (typeof window === "undefined") return;
    if (opts?.debounceMs && opts.debounceMs > 0) {
      const key = event.type;
      const now = Date.now();
      const last = this.lastDebounce.get(key) ?? 0;
      if (now - last < opts.debounceMs) return;
      this.lastDebounce.set(key, now);
    }
    const env: Envelope = { sender: SENDER_ID, event };
    try {
      this.bc?.postMessage(env);
    } catch {
      /* canal pode ter fechado */
    }
    // Fallback storage: escrever então remover dispara `storage` em outras abas.
    try {
      const payload = JSON.stringify(env);
      window.localStorage.setItem(STORAGE_KEY, payload);
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* private mode / quota */
    }
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private handleEnvelope(env: unknown) {
    if (!env || typeof env !== "object") return;
    const e = env as Envelope;
    // Ignora o próprio remetente — evita loops.
    if (!e.event || !e.sender || e.sender === SENDER_ID) return;
    if (typeof e.event.type !== "string") return;
    for (const l of this.listeners) {
      try { l(e.event); } catch { /* listener errors não derrubam outros */ }
    }
  }

  /** Exposto apenas para testes. */
  __debug() {
    return { senderId: SENDER_ID, hasBC: !!this.bc, hasStorage: !!this.boundStorage, listeners: this.listeners.size };
  }
}

export const tabSync = new TabSync();
